import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { SAVE_READ_STATUS_FILTERS } from '../../constants/saveConstants';

// Styled components adapted from FilterPopover.js
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65); /* Darker overlay like typical modals */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001; /* Ensure it's above other content but allows FilterPopover to be above if needed */
`;

const ModalContent = styled.div`
  background: #282E33;
  border-radius: 3px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  padding: 12px;
  width: 280px; /* Matched FilterPopoverContainer */
  max-height: 80vh; /* Keep max-height for scrollable content */
  display: flex;
  flex-direction: column;
  /* Removed responsive styles from FilterPopoverContainer as ModalOverlay handles centering */
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; /* Matched FilterHeader */
`;

const SectionTitle = styled.h4` /* Renamed from ModalTitle for consistency */
  margin: 0; /* Adjusted from FilterPopover's SectionTitle for header use */
  color: #B6C2CF;
  font-size: 14px;
  font-weight: 600;
`;

const SectionLabel = styled.h5`
  margin: 0 0 4px;
  color: #8C9BAB;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CloseButton = styled.button` /* Matched FilterPopover's CloseButton */
  background: none;
  border: none;
  color: #B6C2CF;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 3px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const FilterSection = styled.div` /* Added from FilterPopover */
  margin-bottom: 16px;
  overflow-y: auto; /* Added for scrollability of tag list */
  flex-grow: 1; /* Allows this section to take available space */
`;

const FilterOption = styled.div` /* Renamed from TagListItem, Matched FilterPopover's FilterOption */
  padding: 8px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #B6C2CF;
  font-size: 14px;
  width: 100%;
  border: none;
  font-family: inherit;
  text-align: left;
  box-sizing: border-box;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  background: ${props => props.$selected ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
`;

const StatusSection = styled.div`
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const Checkbox = styled.div` /* Added from FilterPopover */
  width: 16px;
  height: 16px;
  border: 2px solid #B6C2CF;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px; /* Added to space from text if on the right */

  ${props => props.checked && `
    background: #579DFF;
    border-color: #579DFF;

    &:after {
      content: '✓';
      color: white;
      font-size: 12px;
    }
  `}
`;

const LabelIndicator = styled.div` /* Renamed from TagColorCircle, Matched FilterPopover's LabelIndicator */
  min-width: 24px; /* Adjusted for slightly better visual */
  height: 8px; /* Matched LabelIndicator */
  border-radius: 4px; /* Matched LabelIndicator */
  margin-right: 8px;
  background-color: ${props => props.color || '#B6C2CF'};
`;

const OptionContent = styled.div` /* Added from FilterPopover */
  display: flex;
  align-items: center;
  flex-grow: 1;
`;

const ModalFooter = styled.div`
  margin-top: 12px; /* Reduced margin */
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1); /* Lighter border */
  display: flex;
  justify-content: flex-end;
`;

const ClearButton = styled.button`
  background: none; /* More subtle like a link */
  color: #B6C2CF;
  border: none;
  padding: 4px 8px; /* Smaller padding */
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px; /* Slightly smaller */

  &:hover {
    background: rgba(255, 255, 255, 0.1); /* Consistent hover */
  }
`;

const READ_STATUS_OPTIONS = [
  { value: SAVE_READ_STATUS_FILTERS.ALL, label: 'All' },
  { value: SAVE_READ_STATUS_FILTERS.READ, label: 'Read' },
  { value: SAVE_READ_STATUS_FILTERS.UNREAD, label: 'Unread' },
];

const SaveFilterModal = ({
  isOpen,
  onClose,
  allUniqueTags = [],
  selectedTags = [],
  onToggleTagFilter,
  readStatus = SAVE_READ_STATUS_FILTERS.ALL,
  onChangeReadStatus,
  onClearFilters
}) => {
  if (!isOpen) {
    return null;
  }

  const hasTagFilters = selectedTags.length > 0;
  const hasStatusFilter = readStatus !== SAVE_READ_STATUS_FILTERS.ALL;

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const isTagSelected = (tag) => {
    return selectedTags.some(selected =>
      selected.name.toLowerCase() === tag.name.toLowerCase()
    );
  };

  return ReactDOM.createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <SectionTitle>Filters</SectionTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <StatusSection>
          <SectionLabel>Read Status</SectionLabel>
          {READ_STATUS_OPTIONS.map((option) => {
            const selected = readStatus === option.value;
            return (
              <FilterOption
                key={option.value}
                as="button"
                type="button"
                $selected={selected}
                aria-pressed={selected}
                onClick={() => onChangeReadStatus && onChangeReadStatus(option.value)}
              >
                <OptionContent>{option.label}</OptionContent>
                <Checkbox checked={selected} />
              </FilterOption>
            );
          })}
        </StatusSection>
        <FilterSection>
          <SectionLabel>Tags</SectionLabel>
          {allUniqueTags.length > 0 ? (
            allUniqueTags.map((tag) => {
              const checked = isTagSelected(tag);
              return (
                <FilterOption
                  key={tag.name}
                  $selected={checked}
                  onClick={() => onToggleTagFilter(tag)}
                >
                  <OptionContent>
                    <LabelIndicator color={tag.color} />
                    {tag.name}
                  </OptionContent>
                  <Checkbox checked={checked} />
                </FilterOption>
              );
            })
          ) : (
            <div style={{ color: '#8C9BAB', fontSize: '14px', padding: '8px' }}>
              No tags available for filtering.
            </div>
          )}
        </FilterSection>
        {(hasTagFilters || hasStatusFilter) && ( // Only show clear button if filters are active
            <ModalFooter>
                <ClearButton onClick={handleClearFilters}>Clear Filters</ClearButton>
            </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default SaveFilterModal;
